import Link from 'next/link'
import {useTranslation} from 'next-i18next'
import React, {useRef, useState} from 'react'
import {Box, Container, TwoColumns} from '@miro-site/core'
import {
  CardGrid,
  CategoryGroup,
  CategoryHeader,
  HighlightedCard,
  Main,
  TemplateCard,
  TemplateCardProps,
} from '@miro-site/templates-design'
import {SelectedTemplate, TemplateLoader} from '@mirohq-internal/website-ui-foundation'
import ctaImage from '../../public/static/images/community-templates-creators.png'
import {TSS} from '../data/api/tss'
import {TemplateHighlightedCardData} from '../data/mappers/toHighlightedCard'
import {TemplateCardData} from '../data/mappers/toTemplateCard'
import TemplatesSearch from './TemplatesSearch'

export interface MainPageCategorySectionProps {
  title: string
  totalCount: number
  templates: TemplateCardData[]
  renderLink?: (
    defaultLink: React.ReactElement,
    defaultLabel: string,
    categoryPath: string,
  ) => React.ReactNode
  categoryPath: string
}

export type MainPageProps = {
  title: string
  description: string
  categoryGroups: CategoryGroup[]
  highlightedTemplates?: TemplateHighlightedCardData[]
  categories?: MainPageCategorySectionProps[]
}

const MainPage = (props: MainPageProps): React.ReactElement => {
  const {title, description, categoryGroups, highlightedTemplates = [], categories = []} = props

  const headerRef = useRef<HTMLElement>(null)

  const [selectedTemplate, setSelectedTemplate] = useState<SelectedTemplate>({
    metadataId: null,
  })

  const {t} = useTranslation('common')

  const getActionButtonProps = ({
    entryId,
    metadataId,
    newBoardMetadataId,
    accessConstraints,
  }: TemplateCardData): TemplateCardProps['actionButtonProps'] => {
    if (accessConstraints?.requiredPlans?.length > 0) {
      return {
        children: t('templates-actions-open-template'),
        onClick: e => {
          e.preventDefault()
          setSelectedTemplate({entryId, metadataId})
        },
      }
    }

    return {
      onClick: e => {
        e.preventDefault()
        setSelectedTemplate({entryId, metadataId: newBoardMetadataId})
      },
    }
  }

  const handleOnTemplateCopy = (copiedTemplateEntryId: string) => {
    TSS.copy(copiedTemplateEntryId)
  }

  return (
    <>
      <main data-selected-template-id={selectedTemplate.metadataId}>
        <Box $mt={30} $mb={80}>
          <TemplatesSearch categoryGroups={categoryGroups} stickyFixingPoint={headerRef} />
        </Box>
        <Container ref={headerRef}>
          <Main.Title>{title}</Main.Title>
          <Main.Description>{description}</Main.Description>
        </Container>
        {highlightedTemplates.length > 0 && (
          <CardGrid isHighlighted={true}>
            {highlightedTemplates.map((template, i) => (
              <HighlightedCard
                templateActions={{
                  PREVIEW: t('templates-actions-preview'),
                  USE_TEMPLATE: t('templates-actions-use-template'),
                  SHOW_MORE_INFO: t('templates-actions-more-info'),
                }}
                key={template.title}
                previewButton={defaultButton => (
                  <Link href={template.templatePagePath} passHref legacyBehavior>
                    {defaultButton}
                  </Link>
                )}
                actionButtonProps={getActionButtonProps(template)}
                position={i}
                {...template}
              />
            ))}
          </CardGrid>
        )}
        {categories.map((categorySection, i) => (
          <React.Fragment key={categorySection.title}>
            <CardGrid
              top={
                <Link href={categorySection.categoryPath} passHref legacyBehavior>
                  <CategoryHeader
                    data-testid="view-all-link"
                    viewAllLabel={t('view-all-link', {
                      count: categorySection.totalCount,
                    })}
                    title={categorySection.title}
                  />
                </Link>
              }
            >
              {categorySection.templates.map(template => (
                <TemplateCard
                  templateActions={{
                    PREVIEW: t('templates-actions-preview'),
                    USE_TEMPLATE: t('templates-actions-use-template'),
                    SHOW_MORE_INFO: t('templates-actions-more-info'),
                  }}
                  key={template.title}
                  previewButton={defaultButton => (
                    <Link href={template.templatePagePath} passHref legacyBehavior>
                      {defaultButton}
                    </Link>
                  )}
                  actionButtonProps={getActionButtonProps(template)}
                  {...template}
                />
              ))}
            </CardGrid>

            {i === 0 && (
              <TwoColumns
                headline={t('miroverse-cta-headline')}
                content={t('miroverse-cta-description')}
                button={{
                  href: '/miroverse',
                  children: t('miroverse-cta-button'),
                  $buttonType: 'primary',
                  $size: 'medium',
                  $withArrow: true,
                }}
                image={{
                  width: 550,
                  height: 550,
                  name: t('miroverse-cta-headline'),
                  original: {
                    png: ctaImage.src,
                  },
                }}
              />
            )}
          </React.Fragment>
        ))}
      </main>
      <TemplateLoader
        selectedTemplate={selectedTemplate}
        setSelectedTemplate={setSelectedTemplate}
        onTemplateCopy={handleOnTemplateCopy}
      />
    </>
  )
}

export default MainPage
